import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Country, Department, Lang } from '@/data/types';
import { COUNTRIES, DEPARTMENTS } from '@/data';
import { useUser } from '@/auth/AuthContext';
import { LANG_FLAGS, LANG_NAMES, LANG_ORDER } from '@/lib/i18n';
import { KBIcon } from '@/components/kbIcons';
import { NotifBell } from '@/components/NotifBell';
import { SettingsPanel, useTextSize } from '@/components/SettingsMenu';
import type { PortalNotif } from '@/hooks/useNotifications';

const JUR_LABEL: Record<Lang, string> = { en: 'Jurisdiction', es: 'Jurisdicción', fr: 'Juridiction', pt: 'Jurisdição' };
const DEPT_LABEL: Record<Lang, string> = { en: 'Department', es: 'Departamento', fr: 'Département', pt: 'Departamento' };
const LANG_LABEL: Record<Lang, string> = { en: 'Language', es: 'Idioma', fr: 'Langue', pt: 'Idioma' };
const LOGOUT_LABEL: Record<Lang, string> = { en: 'Log out', es: 'Salir', fr: 'Quitter', pt: 'Sair' };
const SETTINGS_LABEL: Record<Lang, string> = { en: 'Settings', es: 'Ajustes', fr: 'Réglages', pt: 'Definições' };

interface Props {
  country: Country;
  dept: Department;
  lang: Lang;
  setLang: (l: Lang) => void;
  onChangeCountry: (c: Country) => void;
  onChangeDept: (d: Department) => void;
  /** Jurisdictions this user may switch to. When only one, the picker is locked
   *  (roster members stay in their own country; super admins get all three). */
  countries?: Country[];
  /** Light/dark: in the settings wheel since 29 Oct 2026 («el modo claro u
   *  oscuro dentro de la rueda»), with the rest of the person's settings. */
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  isSuper: boolean;
  onOpenNotifications: () => void;
  onGoNotification: (n: PortalNotif) => void;
  /** The signed-in person's own department, from the roster (Corporate,
   *  Immigration, Tax…). The chip says THIS, not the knowledge base's
   *  department — which only has one, Immigration, and put Penélope, who is
   *  Corporate, under Immigration (her feedback, 17 and 21 Sept). */
  personDept?: string;
  /** Google Chat: conversations with something unread (silenced ones not
   *  counted), and the way into it. Next to the bell (Gerónimo, 1 Oct 2026). */
  chatUnread?: number;
  onOpenChat?: () => void;
}

/** Floating frosted top bar — brand + jurisdiction/department picker, language
 *  menu (our 4 languages) and sign-out. Replaces the old left sidebar chrome. */
export function TopBar({
  country, dept, lang, setLang, onChangeCountry, onChangeDept, countries,
  theme, onToggleTheme, isSuper, onOpenNotifications, onGoNotification, personDept, chatUnread = 0, onOpenChat,
}: Props) {
  const [ctxOpen, setCtxOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  /* The settings wheel (29 Oct 2026); its text size applies even closed. */
  const [setOpen, setSetOpen] = useState(false);
  const [textSize, setTextSize] = useTextSize();
  /* Signing out asks first: the door button sits beside four harmless icons,
     and one stray click was ending the whole session. */
  const [askOut, setAskOut] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { authConfigured, signOut } = useUser();

  // Countries this user may pick between. Departments are only offered when the
  // user can actually switch (i.e. more than one jurisdiction) — otherwise the
  // context pill is a locked, read-only badge.
  const pickCountries = countries && countries.length ? countries : COUNTRIES;
  const canSwitch = pickCountries.length > 1 || DEPARTMENTS.length > 1;

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setCtxOpen(false);
        setLangOpen(false);
        setSetOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <header className="topbar">
      {/* Brand lives in the left sidebar now — keep an empty spacer so the
          controls stay right-aligned (space-between). */}
      <div className="tb-brand" aria-hidden="true" />

      <div className="tb-right" ref={ref}>
        <div className="tb-pop">
          <button
            className={`tb-context ${ctxOpen ? 'open' : ''} ${canSwitch ? '' : 'locked'}`}
            onClick={() => { if (!canSwitch) return; setCtxOpen((o) => !o); setLangOpen(false); setSetOpen(false); }}
            title={canSwitch ? undefined : (lang === 'es' ? 'Jurisdicción asignada' : lang === 'fr' ? 'Juridiction assignée' : 'Assigned jurisdiction')}
          >
            <span className="tb-flag">{country.flag}</span>
            <span className="tb-ctx-text">
              <span className="tb-ctx-country">{country.name}</span>
              <span className="tb-ctx-dept">{personDept || dept.name}</span>
            </span>
            {canSwitch && <span className="tb-chev"><KBIcon name="chevron" size={16} /></span>}
          </button>

          {ctxOpen && canSwitch && (
            <div className="tb-menu tb-menu-left">
              <div className="tb-menu-label">{JUR_LABEL[lang]}</div>
              <div className="tb-menu-list">
                {pickCountries.map((c) => (
                  <button
                    key={c.code}
                    className={`tb-opt ${c.code === country.code ? 'active' : ''}`}
                    onClick={() => { onChangeCountry(c); setCtxOpen(false); }}
                  >
                    <span className="tb-flag sm">{c.flag}</span>
                    <span className="tb-opt-name">{c.name}</span>
                    {c.code === country.code && <span className="tb-opt-check"><KBIcon name="check" size={15} /></span>}
                  </button>
                ))}
              </div>

              {DEPARTMENTS.length > 1 && (
                <>
                  <div className="tb-menu-label">{DEPT_LABEL[lang]}</div>
                  <div className="tb-menu-list">
                    {DEPARTMENTS.map((d) => (
                      <button
                        key={d.code}
                        className={`tb-opt ${d.code === dept.code ? 'active' : ''}`}
                        onClick={() => { onChangeDept(d); setCtxOpen(false); }}
                      >
                        <span className="tb-dept-glyph">{d.icon}</span>
                        <span className="tb-opt-name">{d.name}</span>
                        {d.code === dept.code && <span className="tb-opt-check"><KBIcon name="check" size={15} /></span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {onOpenChat && (
          <button
            className={`tb-icon-btn nb-btn tb-chat ${chatUnread > 0 ? 'has' : ''}`}
            onClick={onOpenChat}
            data-tip-h="Google Chat"
            data-tip={chatUnread > 0
              ? { en: `${chatUnread} unread`, es: `${chatUnread} sin leer`, fr: `${chatUnread} non lus`, pt: `${chatUnread} por ler` }[lang]
              : { en: 'Messages with your colleagues', es: 'Mensajes con tus compañeros', fr: 'Messages avec vos collègues', pt: 'Mensagens com os colegas' }[lang]}
            aria-label="Google Chat"
          >
            <KBIcon name="chat" size={18} />
            {chatUnread > 0 && <span className="nb-dot" key={chatUnread}>{chatUnread > 9 ? '9+' : chatUnread}</span>}
          </button>
        )}
        <NotifBell lang={lang} isSuper={isSuper} onOpenAll={onOpenNotifications} onGo={onGoNotification} />

        <div className="tb-pop">
          <button
            className="tb-icon-btn"
            onClick={() => { setLangOpen((o) => !o); setCtxOpen(false); setSetOpen(false); }}
            title={LANG_LABEL[lang]}
          >
            <KBIcon name="lang" size={18} />
            <span className="tb-lang-code">{lang.toUpperCase()}</span>
          </button>

          {langOpen && (
            <div className="tb-menu tb-menu-right">
              <div className="tb-menu-label">{LANG_LABEL[lang]}</div>
              <div className="tb-menu-list">
                {LANG_ORDER.map((l) => (
                  <button
                    key={l}
                    className={`tb-opt ${l === lang ? 'active' : ''}`}
                    onClick={() => { setLang(l); setLangOpen(false); }}
                  >
                    <span className="tb-flag sm">{LANG_FLAGS[l]}</span>
                    <span className="tb-opt-name">{LANG_NAMES[l]}</span>
                    {l === lang && <span className="tb-opt-check"><KBIcon name="check" size={15} /></span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="tb-pop">
          <button
            className={`tb-icon-btn ghost ${setOpen ? 'open' : ''}`}
            onClick={() => { setSetOpen((o) => !o); setCtxOpen(false); setLangOpen(false); }}
            title={SETTINGS_LABEL[lang]}
            aria-label={SETTINGS_LABEL[lang]}
            aria-expanded={setOpen}
          >
            <KBIcon name="settings" size={18} />
          </button>
          {setOpen && (
            <div className="tb-menu tb-menu-right tb-menu-set">
              <SettingsPanel lang={lang} size={textSize} setSize={setTextSize} theme={theme} onToggleTheme={onToggleTheme} />
            </div>
          )}
        </div>

        {authConfigured && (
          <button className="tb-icon-btn ghost" onClick={() => setAskOut(true)} title={LOGOUT_LABEL[lang]}>
            <KBIcon name="logout" size={18} />
          </button>
        )}
      </div>
      {/* PORTALED to <body> on purpose: the frosted bar's backdrop-filter
          creates a containing block, so a fixed overlay rendered inside it
          centred on the bar, not the screen. */}
      {askOut && createPortal(
        <div className="lo-backdrop" onClick={() => setAskOut(false)}>
          <div className="lo-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <strong>{lang === 'es' ? '¿Cerrar sesión?' : lang === 'fr' ? 'Se déconnecter ?' : lang === 'pt' ? 'Terminar sessão?' : 'Sign out?'}</strong>
            <div className="lo-actions">
              <button className="lo-stay" onClick={() => setAskOut(false)}>
                {lang === 'es' ? 'Quedarme' : lang === 'fr' ? 'Rester' : lang === 'pt' ? 'Ficar' : 'Stay'}
              </button>
              <button className="lo-go" onClick={() => { setAskOut(false); void signOut(); }}>
                {lang === 'es' ? 'Salir' : lang === 'fr' ? 'Sortir' : lang === 'pt' ? 'Sair' : 'Sign out'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}
